import { useMemo } from 'react';

interface PaginationProps {
    totalItems: number;
    itemsPerPage: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    maxPagesToShow?: number;
    className?: string;
    showFirstLastButtons?: boolean;
    disabled?: boolean;
}

const Pagination = ({
    totalItems,
    itemsPerPage,
    currentPage,
    onPageChange,
    maxPagesToShow = 5,
    className = '',
    showFirstLastButtons = true,
    disabled = false
}: PaginationProps) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Generate the array of page numbers to display
    const pages = useMemo<number[]>(() => {
        if (totalPages <= maxPagesToShow) {
            // If total pages is less than max pages to show, display all pages
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        let startPage: number;
        let endPage: number;

        // Calculate the start and end page numbers based on current page position
        if (currentPage <= Math.ceil(maxPagesToShow / 2)) {
            // Current page is near the start
            startPage = 1;
            endPage = maxPagesToShow;
        } else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) {
            // Current page is near the end
            startPage = totalPages - maxPagesToShow + 1;
            endPage = totalPages;
        } else {
            // Current page is in the middle
            startPage = currentPage - Math.floor(maxPagesToShow / 2);
            endPage = currentPage + Math.floor(maxPagesToShow / 2);
        }

        // Create the array of page numbers to display
        return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    }, [currentPage, totalPages, maxPagesToShow]);

    if (totalPages <= 1) {
        return null; // Don't show pagination if there's only one page
    }

    // Handle page change
    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages || page === currentPage || disabled) {
            return;
        }

        onPageChange(page);
    };

    return (
        <nav aria-label="Page navigation" className={className}>
            <ul className="flex items-center -space-x-px h-10 text-base">
                {/* First page button */}
                {showFirstLastButtons && (
                    <li>
                        <button
                            aria-label="Go to first page"
                            className={`flex items-center justify-center px-4 h-10 ml-0 leading-tight rounded-l-lg border ${
                                currentPage === 1 || disabled
                                    ? 'bg-zinc-700 text-zinc-400 border-zinc-700 cursor-not-allowed'
                                    : 'bg-dark text-zinc-400 border-zinc-700 hover:bg-zinc-700 hover:text-white'
                            }`}
                            disabled={currentPage === 1 || disabled}
                            onClick={() => handlePageChange(1)}
                        >
                            &laquo;
                        </button>
                    </li>
                )}

                {/* Previous button */}
                <li>
                    <button
                        aria-label="Go to previous page"
                        className={`flex items-center justify-center px-4 h-10 leading-tight border ${
                            !showFirstLastButtons ? 'rounded-l-lg' : ''
                        } ${
                            currentPage === 1 || disabled
                                ? 'bg-zinc-700 text-zinc-400 border-zinc-700 cursor-not-allowed'
                                : 'bg-dark text-zinc-400 border-zinc-700 hover:bg-zinc-700 hover:text-white'
                        }`}
                        disabled={currentPage === 1 || disabled}
                        onClick={() => handlePageChange(currentPage - 1)}
                    >
                        &lsaquo;
                    </button>
                </li>

                {/* Page numbers */}
                {pages.map((page) => (
                    <li key={page}>
                        <button
                            aria-current={page === currentPage ? 'page' : undefined}
                            className={`flex items-center justify-center px-4 h-10 leading-tight border ${
                                page === currentPage
                                    ? 'bg-p314-blue text-white border-p314-blue'
                                    : disabled
                                      ? 'bg-zinc-700 text-zinc-400 border-zinc-700 cursor-not-allowed'
                                      : 'bg-dark text-zinc-400 border-zinc-700 hover:bg-zinc-700 hover:text-white'
                            }`}
                            disabled={disabled}
                            onClick={() => handlePageChange(page)}
                        >
                            {page}
                        </button>
                    </li>
                ))}

                {/* Next button */}
                <li>
                    <button
                        aria-label="Go to next page"
                        className={`flex items-center justify-center px-4 h-10 leading-tight border ${
                            !showFirstLastButtons ? 'rounded-r-lg' : ''
                        } ${
                            currentPage === totalPages || disabled
                                ? 'bg-zinc-700 text-zinc-400 border-zinc-700 cursor-not-allowed'
                                : 'bg-dark text-zinc-400 border-zinc-700 hover:bg-zinc-700 hover:text-white'
                        }`}
                        disabled={currentPage === totalPages || disabled}
                        onClick={() => handlePageChange(currentPage + 1)}
                    >
                        &rsaquo;
                    </button>
                </li>

                {/* Last page button */}
                {showFirstLastButtons && (
                    <li>
                        <button
                            aria-label="Go to last page"
                            className={`flex items-center justify-center px-4 h-10 leading-tight rounded-r-lg border ${
                                currentPage === totalPages || disabled
                                    ? 'bg-zinc-700 text-zinc-400 border-zinc-700 cursor-not-allowed'
                                    : 'bg-dark text-zinc-400 border-zinc-700 hover:bg-zinc-700 hover:text-white'
                            }`}
                            disabled={currentPage === totalPages || disabled}
                            onClick={() => handlePageChange(totalPages)}
                        >
                            &raquo;
                        </button>
                    </li>
                )}
            </ul>
        </nav>
    );
};

export default Pagination;
